import { useQuery } from "@tanstack/react-query";
import { list } from "./service";
import EventSettings from "./EventSettings";

import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  TableActionButton,
  TableActionsCell,
} from "../../common/adminTable";
import { buildPaginationMeta } from "../../../utils/clientPagination";
import { formatListingDate } from "../../../utils/formatListingDate";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";

import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useTableSearch from "../../../hooks/useTableSearch";

const Events = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [showToaster, setShowToaster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterType, setToasterType] = useState("success");

  const [showStatus, setShowStatus] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  /* search and filter states */
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["events", currentPage, activeSearch],
    queryFn: () => list(currentPage, activeSearch),
    keepPreviousData: true,
  });

  const clearFilters = useCallback(() => {
    clearSearch();
    setCurrentPage(1);
  }, [clearSearch]);

  const events = response?.data || [];
  const current_page = response?.current_page || 1;
  const last_page = response?.last_page || 1;
  const total = response?.total || 0;
  const per_page = response?.per_page || 10;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  const pageMeta = ADMIN_PAGE_META.events;

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin h-8 w-8 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="text-primary font-medium">Loading events...</span>
            </div>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout>
        <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />
        <AdminPageCard>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="fi fi-rr-exclamation text-red-500 text-4xl mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Error loading events
              </h3>
              <p className="text-gray-600">{error.message}</p>
            </div>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  const openStatus = (event) => {
    setSelectedEvent(event);
    setShowStatus(true);
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <TableSearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search events..."
          />
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination pagination={pagination} onPageChange={handlePageChange} />
            </AdminTableFooter>
          }
        >
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Event Name</AdminTableTh>
          <AdminTableTh>Supplier</AdminTableTh>
          <AdminTableTh>Category</AdminTableTh>
          <AdminTableTh>Duration</AdminTableTh>
          <AdminTableTh>Location</AdminTableTh>
          <AdminTableTh>Event Date Period</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {events.length === 0 ? (
            <TableEmptyRow
              colSpan={9}
              icon="fi fi-rr-calendar"
              emptyMessage="No events found"
              searchQuery={activeSearch}
              onClearSearch={clearFilters}
            />
          ) : (
            events.map((event, index) => (
              <AdminTableRow key={event.id}>
                <AdminTableCell>
                  {(current_page - 1) * 10 + index + 1}
                </AdminTableCell>
                <AdminTableCell className="font-medium">
                  {event.name || event.title || "Untitled Event"}
                </AdminTableCell>
                <AdminTableCell>
                  <div>
                    <p className="font-medium">{event.user?.name}</p>
                    <p className="text-xs text-gray-500">
                      {event.user?.phone}
                    </p>
                  </div>
                </AdminTableCell>
                <AdminTableCell>
                  {event.event_category_master?.name || "N/A"}
                </AdminTableCell>
                <AdminTableCell>{event.duration || "N/A"}</AdminTableCell>
                <AdminTableCell>
                  {event.location || event.address || "N/A"}
                </AdminTableCell>
                <AdminTableCell>
                  <div>
                    <p className="text-xs text-gray-600">
                      From: {formatListingDate(event.starting_date)}
                    </p>
                    <p className="text-xs text-gray-600">
                      To: {formatListingDate(event.ending_date)}
                    </p>
                  </div>
                </AdminTableCell>
                <AdminTableCell>
                  <div className="flex flex-col gap-1">
                    {event.is_approved === 1 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-600 mr-1.5"></span>
                        Approved
                      </span>
                    ) : event.is_approved === 2 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-600 mr-1.5"></span>
                        Rejected
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-600 mr-1.5"></span>
                        Pending
                      </span>
                    )}
                    {event.popular === 1 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-600">
                        Popular
                      </span>
                    )}
                    {event.recommended === 1 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
                        Recommended
                      </span>
                    )}
                  </div>
                </AdminTableCell>
                <TableActionsCell>
                  <TableActionButton
                    variant="edit"
                    showLabel={false}
                    onClick={() => navigate(`/events/${event.id}/edit`)}
                  />
                  <TableActionButton
                    variant="settings"
                    showLabel={false}
                    onClick={() => openStatus(event)}
                  />
                </TableActionsCell>
              </AdminTableRow>
            ))
          )}
        </AdminTableBody>
      </AdminTable>
      </AdminPageCard>

      {showStatus && (
        <EventSettings
          open={showStatus}
          onClose={() => {
            setShowStatus(false);
            setSelectedEvent(null);
          }}
          eventData={selectedEvent}
        />
      )}

      {showToaster && (
        <Toster
          message={toasterMessage}
          type={toasterType}
          duration={3000}
          onClose={() => setShowToaster(false)}
        />
      )}
    </AdminPageLayout>
  );
};

export default Events;
